import { useState, useCallback } from 'react';
import { getParallelsForType, parallelKey } from './parallelData';

const STATUS_ICONS = {
  collected: { symbol: '✓', label: 'Collected', color: '#00e87a' },
  wishlist:  { symbol: '★', label: 'Wishlist',  color: '#f0c040' },
  trade:     { symbol: '⇄', label: 'For Trade', color: '#00d4ff' },
  for_sale:  { symbol: '$', label: 'For Sale',  color: '#b060ff' },
};

const SET_OPTIONS = [
  { id: 'base',   label: 'Base Set' },
  { id: 'insert', label: 'Inserts' },
  { id: 'auto',   label: 'Autographs' },
  { id: 'sketch', label: 'Sketch' },
];

const STATUS_OPTIONS = [
  { id: 'all',       label: 'All Cards' },
  { id: 'collected', label: '✓ Collected Only' },
  { id: 'wishlist',  label: '★ Wishlist Only' },
  { id: 'trade',     label: '⇄ For Trade Only' },
  { id: 'for_sale',  label: '$ For Sale Only' },
  { id: 'marked',    label: 'All Marked Cards' },
];

export default function PrintChecklist({ profile, setMeta, SETS, SET_PARALLELS = [], cardStates, gradings, salePrices, onClose }) {
  const [selectedTypes, setSelectedTypes] = useState(['base']);
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeParallels, setIncludeParallels] = useState(false);
  const [selectedParallels, setSelectedParallels] = useState([]);
  const [showArtist, setShowArtist] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showGrade, setShowGrade] = useState(true);
  const [columnsPerPage, setColumnsPerPage] = useState(3);

  const toggleType = (id) => setSelectedTypes(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  // Build card rows for PDF
  const buildRows = useCallback(() => {
    const rows = [];
    const visibleSets = SETS.filter(s => selectedTypes.includes(s.type));

    visibleSets.forEach(set => {
      const parallels = SET_PARALLELS && SET_PARALLELS.length > 0 ? SET_PARALLELS : getParallelsForType(set.type);
      set.cards.forEach(card => {
        const key = `${setMeta.id}__${set.id}__${card.n}`;
        const state = cardStates[key] || null;
        const grading = gradings?.[key] || null;
        const price = salePrices?.[key] || null;

        const passes =
          statusFilter === 'all' ||
          (statusFilter === 'marked' && state) ||
          state === statusFilter;

        if (passes) {
          rows.push({ card, key, state, grading, price, setType: set.type, parallel: null });
        }

        // Include parallels if enabled
        if (includeParallels) {
          parallels.forEach(parallel => {
            if (selectedParallels.length > 0 && !selectedParallels.includes(parallel.id)) return;
            const pKey = parallelKey(`${setMeta.id}__${set.id}`, card.n, parallel.id);
            const pState = cardStates[pKey] || null;
            const pGrading = gradings?.[pKey] || null;
            const pPrice = salePrices?.[pKey] || null;

            const pPasses =
              statusFilter === 'all' ||
              (statusFilter === 'marked' && pState) ||
              pState === statusFilter;

            if (pPasses) {
              rows.push({ card, key: pKey, state: pState, grading: pGrading, price: pPrice, setType: set.type, parallel });
            }
          });
        }
      });
    });

    return rows;
  }, [SETS, selectedTypes, statusFilter, cardStates, gradings, salePrices, setMeta.id, includeParallels, selectedParallels]);

  const rows = buildRows();
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const handlePrint = () => {
    const printContent = buildPrintHTML(rows, {
      profile, setMeta, today, statusFilter,
      showArtist, showPrice, showGrade, columnsPerPage,
    });

    const win = window.open('', '_blank');
    win.document.write(printContent);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 500);
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>PRINT / SAVE PDF</div>
            <div style={styles.subtitle}>Generate a branded checklist PDF</div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          {/* Set types */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>INCLUDE SETS</div>
            <div style={styles.optionRow}>
              {SET_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  style={{
                    ...styles.optionBtn,
                    borderColor: selectedTypes.includes(opt.id) ? '#00d4ff' : '#1a2540',
                    color: selectedTypes.includes(opt.id) ? '#00d4ff' : '#7a9ab8',
                    background: selectedTypes.includes(opt.id) ? 'rgba(0,212,255,0.08)' : 'transparent',
                  }}
                  onClick={() => toggleType(opt.id)}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Status filter */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>SHOW CARDS</div>
            <div style={styles.optionRow}>
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  style={{
                    ...styles.optionBtn,
                    borderColor: statusFilter === opt.id ? '#c9a227' : '#1a2540',
                    color: statusFilter === opt.id ? '#c9a227' : '#7a9ab8',
                    background: statusFilter === opt.id ? 'rgba(201,162,39,0.08)' : 'transparent',
                  }}
                  onClick={() => setStatusFilter(opt.id)}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Parallels */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>PARALLELS</div>
            <div style={styles.optionRow}>
              <button
                style={{
                  ...styles.optionBtn,
                  borderColor: includeParallels ? '#f0c040' : '#1a2540',
                  color: includeParallels ? '#f0c040' : '#7a9ab8',
                  background: includeParallels ? 'rgba(240,192,64,0.08)' : 'transparent',
                }}
                onClick={() => setIncludeParallels(v => !v)}
              >{includeParallels ? '✓ Include Parallels' : 'Include Parallels'}</button>
            </div>
            {includeParallels && SETS && (
              <div style={{ ...styles.optionRow, marginTop: '8px' }}>
                <button
                  style={{ ...styles.optionBtn, fontSize: '10px', borderColor: selectedParallels.length === 0 ? '#f0c040' : '#1a2540', color: selectedParallels.length === 0 ? '#f0c040' : '#7a9ab8', background: selectedParallels.length === 0 ? 'rgba(240,192,64,0.08)' : 'transparent' }}
                  onClick={() => setSelectedParallels([])}
                >All Parallels</button>
                {(SET_PARALLELS && SET_PARALLELS.length > 0 ? SET_PARALLELS : getParallelsForType(selectedTypes[0] || 'base')).map(p => (
                  <button
                    key={p.id}
                    style={{ ...styles.optionBtn, fontSize: '10px', borderColor: selectedParallels.includes(p.id) ? p.color : '#1a2540', color: selectedParallels.includes(p.id) ? p.color : '#7a9ab8', background: selectedParallels.includes(p.id) ? p.color + '15' : 'transparent' }}
                    onClick={() => setSelectedParallels(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                  >{p.name}{p.print_run ? ` /${p.print_run}` : ''}</button>
                ))}
              </div>
            )}
          </div>

          {/* Options */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>OPTIONS</div>
            <div style={styles.optionRow}>
              {[
                { key: 'showArtist', val: showArtist, set: setShowArtist, label: 'Show Artist' },
                { key: 'showGrade', val: showGrade, set: setShowGrade, label: 'Show Grade' },
                { key: 'showPrice', val: showPrice, set: setShowPrice, label: 'Show Price' },
              ].map(opt => (
                <button
                  key={opt.key}
                  style={{
                    ...styles.optionBtn,
                    borderColor: opt.val ? '#00e87a' : '#1a2540',
                    color: opt.val ? '#00e87a' : '#7a9ab8',
                    background: opt.val ? 'rgba(0,232,122,0.08)' : 'transparent',
                  }}
                  onClick={() => opt.set(v => !v)}
                >{opt.val ? `✓ ${opt.label}` : opt.label}</button>
              ))}
            </div>
          </div>

          {/* Columns */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>COLUMNS PER PAGE</div>
            <div style={styles.optionRow}>
              {[2, 3, 4].map(n => (
                <button
                  key={n}
                  style={{
                    ...styles.optionBtn,
                    borderColor: columnsPerPage === n ? '#00d4ff' : '#1a2540',
                    color: columnsPerPage === n ? '#00d4ff' : '#7a9ab8',
                    background: columnsPerPage === n ? 'rgba(0,212,255,0.08)' : 'transparent',
                  }}
                  onClick={() => setColumnsPerPage(n)}
                >{n} Columns</button>
              ))}
            </div>
          </div>

          {/* Preview stats */}
          <div style={styles.previewBar}>
            <span style={styles.previewCount}>{rows.length} cards</span>
            <span style={styles.previewSub}>will be included in your PDF</span>
          </div>

          {/* Legend preview */}
          <div style={styles.legendRow}>
            {Object.entries(STATUS_ICONS).map(([k, v]) => (
              <span key={k} style={styles.legendItem}>
                <span style={{ color: v.color, fontWeight: 700 }}>{v.symbol}</span>
                <span style={styles.legendLabel}>{v.label}</span>
              </span>
            ))}
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={styles.printBtn} onClick={handlePrint} disabled={rows.length === 0}>
            🖨️ OPEN PDF PREVIEW
          </button>
        </div>
      </div>
    </div>
  );
}

function buildPrintHTML(rows, { profile, setMeta, today, statusFilter, showArtist, showPrice, showGrade, columnsPerPage }) {
  const colWidth = Math.floor(100 / columnsPerPage);

  const cardRows = rows.map(({ card, state, grading, price }) => {
    const status = state ? STATUS_ICONS[state] : null;
    const statusCell = status
      ? `<span class="status-badge" style="color:${status.color};border-color:${status.color}20;background:${status.color}10">${status.symbol} ${status.label}</span>`
      : `<span class="status-badge unchecked">○ Unchecked</span>`;

    const artistLine = showArtist && card.artist
      ? `<div class="card-artist">${card.artist}</div>` : '';
    const gradeLine = showGrade && grading
      ? `<div class="card-extra grade">🏆 ${grading.company} ${grading.grade}</div>` : '';
    const priceLine = showPrice && price && state === 'for_sale'
      ? `<div class="card-extra price">$${Number(price).toFixed(2)}</div>` : '';

    return `
      <div class="card-row">
        <div class="card-num">${card.n}</div>
        <div class="card-info">
          <div class="card-name">${card.name}</div>
          ${artistLine}
          ${gradeLine}
          ${priceLine}
        </div>
        <div class="card-status">${statusCell}</div>
      </div>`;
  }).join('');

  // Status summary counts
  const counts = { collected: 0, wishlist: 0, trade: 0, for_sale: 0, unchecked: 0 };
  rows.forEach(({ state }) => {
    if (state && counts[state] !== undefined) counts[state]++;
    else counts.unchecked++;
  });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${profile.username} — ${setMeta.shortName} Checklist</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Arial', sans-serif;
      background: #fff;
      color: #1a1a2e;
      font-size: 11px;
    }

    /* ── HEADER ── */
    .header {
      background: #04060f;
      color: #fff;
      padding: 20px 28px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 3px solid ${setMeta.color};
    }
    .header-left {}
    .brand-line {
      font-size: 8px;
      letter-spacing: 5px;
      color: #c9a227;
      text-transform: uppercase;
      margin-bottom: 4px;
    }
    .header-title {
      font-size: 22px;
      font-weight: 900;
      letter-spacing: 2px;
      color: #fff;
      text-transform: uppercase;
    }
    .header-title span { color: ${setMeta.color}; }
    .header-sub {
      font-size: 9px;
      letter-spacing: 3px;
      color: #7a9ab8;
      margin-top: 3px;
      text-transform: uppercase;
    }
    .header-right { text-align: right; }
    .header-username {
      font-size: 14px;
      font-weight: 700;
      color: ${setMeta.color};
      letter-spacing: 2px;
    }
    .header-date {
      font-size: 9px;
      color: #4a5a7a;
      margin-top: 2px;
      letter-spacing: 1px;
    }
    .header-url {
      font-size: 8px;
      color: #2a3a5a;
      margin-top: 2px;
    }

    /* ── STATS BAR ── */
    .stats-bar {
      background: #f8f9fc;
      border-bottom: 1px solid #e0e4ef;
      padding: 8px 28px;
      display: flex;
      gap: 20px;
      align-items: center;
      flex-wrap: wrap;
    }
    .stat { display: flex; align-items: center; gap: 5px; }
    .stat-icon { font-size: 12px; }
    .stat-num { font-weight: 700; font-size: 12px; }
    .stat-label { color: #666; font-size: 10px; }
    .stat-total .stat-num { color: #1a1a2e; }
    .stat-collected .stat-num { color: #00a854; }
    .stat-wishlist .stat-num { color: #c8a000; }
    .stat-trade .stat-num { color: #0099bb; }
    .stat-sale .stat-num { color: #8040cc; }

    /* ── LEGEND ── */
    .legend {
      padding: 6px 28px;
      border-bottom: 1px solid #e0e4ef;
      display: flex;
      gap: 16px;
      background: #fff;
      flex-wrap: wrap;
    }
    .legend-item { display: flex; align-items: center; gap: 4px; font-size: 10px; color: #444; }
    .legend-badge {
      display: inline-block;
      padding: 1px 6px;
      border-radius: 2px;
      font-size: 9px;
      font-weight: 700;
      border: 1px solid;
    }

    /* ── GRID ── */
    .grid {
      display: flex;
      flex-wrap: wrap;
      padding: 16px 20px;
      gap: 0;
    }
    .column {
      width: ${colWidth}%;
      padding: 0 8px;
      border-right: 1px solid #e8ecf5;
    }
    .column:last-child { border-right: none; }

    /* ── CARD ROW ── */
    .card-row {
      display: flex;
      align-items: flex-start;
      gap: 6px;
      padding: 4px 0;
      border-bottom: 1px solid #f0f2f8;
    }
    .card-row:last-child { border-bottom: none; }
    .card-num {
      font-size: 9px;
      color: #999;
      min-width: 26px;
      padding-top: 1px;
      font-weight: 600;
    }
    .card-info { flex: 1; min-width: 0; }
    .card-name {
      font-size: 10px;
      font-weight: 600;
      color: #1a1a2e;
      line-height: 1.3;
    }
    .card-parallel {
      font-size: 8px;
      font-weight: 700;
      margin-top: 1px;
      letter-spacing: 0.5px;
    }
    .card-artist {
      font-size: 8px;
      color: #999;
      margin-top: 1px;
      font-style: italic;
    }
    .card-extra {
      font-size: 8px;
      margin-top: 1px;
    }
    .card-extra.grade { color: #0099bb; }
    .card-extra.price { color: #8040cc; }
    .card-status { flex-shrink: 0; }

    .status-badge {
      display: inline-block;
      font-size: 8px;
      font-weight: 700;
      padding: 2px 5px;
      border-radius: 2px;
      border: 1px solid;
      white-space: nowrap;
    }
    .status-badge.unchecked {
      color: #ccc;
      border-color: #eee;
      background: #fafafa;
    }

    /* ── FOOTER ── */
    .footer {
      margin-top: 8px;
      padding: 10px 28px;
      border-top: 2px solid #04060f;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fc;
    }
    .footer-brand {
      font-size: 9px;
      letter-spacing: 3px;
      color: #aaa;
      text-transform: uppercase;
    }
    .footer-url {
      font-size: 8px;
      color: #ccc;
    }

    /* ── PRINT ── */
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .stats-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }

    @page { margin: 0.5in; size: letter; }
  </style>
</head>
<body>

  <div class="header">
    <div class="header-left">
      <div class="brand-line">CollectionKeeper · Star Wars</div>
      <div class="header-title">${setMeta.shortName.toUpperCase().replace(' ', ' <span>').replace(/(\d{4})/, '$1</span>')}</div>
      <div class="header-sub">// Official Collector Checklist //</div>
    </div>
    <div class="header-right">
      <div class="header-username">${profile.username}</div>
      <div class="header-date">${today}</div>
      <div class="header-url">collectionkeeper.org/u/${profile.username}</div>
    </div>
  </div>

  <div class="stats-bar">
    <div class="stat stat-total">
      <span class="stat-num">${rows.length}</span>
      <span class="stat-label">Total Cards</span>
    </div>
    <div class="stat stat-collected">
      <span class="stat-icon">✓</span>
      <span class="stat-num">${counts.collected}</span>
      <span class="stat-label">Collected</span>
    </div>
    <div class="stat stat-wishlist">
      <span class="stat-icon">★</span>
      <span class="stat-num">${counts.wishlist}</span>
      <span class="stat-label">Wishlist</span>
    </div>
    <div class="stat stat-trade">
      <span class="stat-icon">⇄</span>
      <span class="stat-num">${counts.trade}</span>
      <span class="stat-label">For Trade</span>
    </div>
    <div class="stat stat-sale">
      <span class="stat-icon">$</span>
      <span class="stat-num">${counts.for_sale}</span>
      <span class="stat-label">For Sale</span>
    </div>
    <div class="stat" style="margin-left:auto;color:#999;">
      <span class="stat-label">${counts.unchecked} unchecked</span>
    </div>
  </div>

  <div class="legend">
    <span class="legend-item"><span class="legend-badge" style="color:#00a854;border-color:#00a85420;background:#00a85410">✓ Collected</span></span>
    <span class="legend-item"><span class="legend-badge" style="color:#c8a000;border-color:#c8a00020;background:#c8a00010">★ Wishlist</span></span>
    <span class="legend-item"><span class="legend-badge" style="color:#0099bb;border-color:#0099bb20;background:#0099bb10">⇄ For Trade</span></span>
    <span class="legend-item"><span class="legend-badge" style="color:#8040cc;border-color:#8040cc20;background:#8040cc10">$ For Sale</span></span>
    <span class="legend-item"><span class="legend-badge" style="color:#ccc;border-color:#eee;background:#fafafa">○ Unchecked</span></span>
  </div>

  <div class="grid">
    ${buildColumns(rows, columnsPerPage, showArtist, showPrice, showGrade)}
  </div>

  <div class="footer">
    <div class="footer-brand">CollectionKeeper.org · Track Your Collection</div>
    <div class="footer-url">Generated ${today}</div>
  </div>

</body>
</html>`;
}

function buildColumns(rows, cols, showArtist, showPrice, showGrade) { // parallel data is in row.parallel
  const perCol = Math.ceil(rows.length / cols);
  let html = '';
  for (let c = 0; c < cols; c++) {
    const slice = rows.slice(c * perCol, (c + 1) * perCol);
    const cards = slice.map(({ card, state, grading, price, parallel }) => {
      const status = state ? STATUS_ICONS[state] : null;
      const statusCell = status
        ? `<span class="status-badge" style="color:${status.color};border-color:${status.color}20;background:${status.color}10">${status.symbol} ${status.label}</span>`
        : `<span class="status-badge unchecked">○</span>`;
      const artistLine = showArtist && card.artist ? `<div class="card-artist">${card.artist}</div>` : '';
      const gradeLine = showGrade && grading ? `<div class="card-extra grade">🏆 ${grading.company} ${grading.grade}</div>` : '';
      const priceLine = showPrice && price && state === 'for_sale' ? `<div class="card-extra price">$${Number(price).toFixed(2)}</div>` : '';
      const parallelLine = parallel ? `<div class="card-parallel" style="color:${parallel.color}">${parallel.name}${parallel.print_run ? ' /' + parallel.print_run : ''}</div>` : '';
      return `<div class="card-row">
        <div class="card-num">${card.n}</div>
        <div class="card-info"><div class="card-name">${card.name}</div>${parallelLine}${artistLine}${gradeLine}${priceLine}</div>
        <div class="card-status">${statusCell}</div>
      </div>`;
    }).join('');
    html += `<div class="column">${cards}</div>`;
  }
  return html;
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000, padding: '20px',
  },
  modal: {
    background: '#080c1a',
    border: '1px solid #1a2540',
    borderRadius: '4px',
    width: '100%', maxWidth: '560px',
    maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid #1a2540',
    background: 'linear-gradient(135deg, rgba(240,192,64,0.06), rgba(0,212,255,0.04))',
    flexShrink: 0,
  },
  title: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '14px', letterSpacing: '3px', color: '#c9a227',
  },
  subtitle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px', color: '#7a9ab8', marginTop: '4px',
  },
  closeBtn: {
    background: 'none', border: '1px solid #1a2540',
    color: '#7a9ab8', cursor: 'pointer', fontSize: '14px',
    width: '32px', height: '32px', borderRadius: '2px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  body: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  section: { marginBottom: '18px' },
  sectionLabel: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '9px', letterSpacing: '2px', color: '#7a9ab8',
    marginBottom: '8px',
  },
  optionRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  optionBtn: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px', padding: '6px 12px',
    border: '1px solid', borderRadius: '2px',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  previewBar: {
    background: '#0a1020',
    border: '1px solid #1a2540',
    borderRadius: '2px',
    padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: '8px',
    marginBottom: '12px',
  },
  previewCount: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '18px', fontWeight: 900, color: '#c9a227',
  },
  previewSub: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px', color: '#7a9ab8',
  },
  legendRow: {
    display: 'flex', gap: '12px', flexWrap: 'wrap',
    padding: '8px 0',
  },
  legendItem: {
    display: 'flex', alignItems: 'center', gap: '5px',
    fontFamily: "'Share Tech Mono', monospace", fontSize: '10px',
  },
  legendLabel: { color: '#7a9ab8' },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #1a2540',
    display: 'flex', gap: '10px', justifyContent: 'flex-end',
    flexShrink: 0,
  },
  cancelBtn: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '10px', letterSpacing: '1px',
    padding: '10px 20px',
    background: 'transparent', border: '1px solid #1a2540',
    color: '#7a9ab8', cursor: 'pointer', borderRadius: '2px',
  },
  printBtn: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px', letterSpacing: '2px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, rgba(201,162,39,0.15), rgba(0,212,255,0.1))',
    border: '1px solid #c9a227', color: '#c9a227',
    cursor: 'pointer', borderRadius: '2px',
  },
};
